import React, { useEffect, useState } from 'react';
import packageService from '../services/packageService';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../shared/providers/LanguageProvider';
import { ShimmerTable, ShimmerBox } from '../components/common/Shimmer';
import { PageHeader } from '@/components/ui/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const initialForm = {
  name: '',
  price: '',
  discount_percentage: '',
  description: '',
  validity_days: '',
  is_active: true,
};

const Packages = () => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchPackages = async () => {
    setLoading(true);
    try {
      const data = await packageService.getPackages(token);
      setPackages(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPackages();
    // eslint-disable-next-line
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCreate = () => {
    setForm(initialForm);
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (pkg) => {
    setForm({ ...pkg });
    setEditingId(pkg.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('areYouSureDeletePackage'))) return;
    setActionLoading(true);
    try {
      await packageService.deletePackage(token, id);
      setPackages((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      if (editingId) {
        const updated = await packageService.updatePackage(token, editingId, form);
        setPackages((prev) => prev.map((p) => (p.id === editingId ? updated : p)));
      } else {
        const created = await packageService.createPackage(token, form);
        setPackages((prev) => [created, ...prev]);
      }
      setShowForm(false);
      setForm(initialForm);
      setEditingId(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('packagesTitle')}
        actions={
          <Button onClick={handleCreate}>{t('addPackage')}</Button>
        }
      />

      {error && <p className="text-destructive">{error}</p>}

      {loading ? (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <ShimmerBox width="w-64" height="h-10" />
            <ShimmerBox width="w-32" height="h-10" />
          </div>
          <ShimmerTable rows={8} columns={7} />
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('name')}</TableHead>
                  <TableHead>{t('price') || 'Price'}</TableHead>
                  <TableHead>{t('discountPercent')}</TableHead>
                  <TableHead>{t('description')}</TableHead>
                  <TableHead>{t('validityDays')}</TableHead>
                  <TableHead>{t('active')}</TableHead>
                  <TableHead>{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell>{pkg.name}</TableCell>
                    <TableCell>{pkg.price}</TableCell>
                    <TableCell>{pkg.discount_percentage}</TableCell>
                    <TableCell className="max-w-xs truncate">{pkg.description}</TableCell>
                    <TableCell>{pkg.validity_days}</TableCell>
                    <TableCell>
                      <Badge variant={pkg.is_active ? 'success' : 'secondary'}>
                        {pkg.is_active ? t('activeYes') : t('activeNo')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(pkg)}
                        >
                          {t('edit')}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => handleDelete(pkg.id)}
                          disabled={actionLoading}
                        >
                          {t('delete')}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t('editPackage') : t('addPackage')}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('name')}</Label>
              <Input
                id="name"
                type="text"
                name="name"
                value={form.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="price">{t('price') || 'Price'}</Label>
              <Input
                id="price"
                type="number"
                name="price"
                value={form.price}
                onChange={handleInputChange}
                required
                min="0"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="discount_percentage">{t('discountPercent')}</Label>
              <Input
                id="discount_percentage"
                type="number"
                name="discount_percentage"
                value={form.discount_percentage}
                onChange={handleInputChange}
                required
                min="0"
                max="100"
                step="0.01"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">{t('description')}</Label>
              <Textarea
                id="description"
                name="description"
                value={form.description}
                onChange={handleInputChange}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="validity_days">{t('validityDays')}</Label>
              <Input
                id="validity_days"
                type="number"
                name="validity_days"
                value={form.validity_days}
                onChange={handleInputChange}
                min="0"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={!!form.is_active}
                onCheckedChange={(checked) => setForm(prev => ({ ...prev, is_active: checked }))}
              />
              <Label htmlFor="is_active">{t('active')}</Label>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForm(false)}
                disabled={actionLoading}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {editingId ? t('update') : t('create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Packages;
